/**
 * Passport field provenance tracker + extraction guard (host side).
 *
 * The Hub owns canonical promotion and decides, server-side, which passport
 * values replace canonical applicant data (saved valid manual value first,
 * validated OCR as fallback). The portal's job is to give it honest
 * operational evidence about HOW each passport field was captured — and to
 * make sure a late or obsolete OCR response can never overwrite what the
 * applicant typed. Both concerns live here as pure, framework-free logic so
 * they can be tested without the form engine.
 *
 * What this tracks per field:
 *   origin        — 'ocr' (written by an accepted extraction), 'manual' (the
 *                   applicant changed it), 'hydrated' (restored from a saved
 *                   snapshot whose origin is none of the others), 'unknown'
 *                   (value present but no evidence — legacy drafts / Globus
 *                   step data; survives draft round-trips unchanged).
 *   userEdited    — persistent: the applicant changed this field at some point
 *                   since the last successful save. Survives draft reload.
 *   editedThisSession — transient: changed since this mount/hydration. Never
 *                   persisted; cleared on hydrate/reset/successful save.
 *   extractionVersion — the extraction request that wrote an 'ocr' value.
 *
 * What this deliberately does NOT do: decide business policy (nationality
 * precedence, birth-city normalisation), store OCR payloads, or mint document
 * or evidence ids. The save metadata it produces is non-tamper-proof input
 * evidence; the Hub derives accepted attribution itself.
 *
 * Attribution is intentionally event-based. Whole-value `customOnChange`
 * snapshots (the only signal in 2.45.x) cannot establish who
 * made a write. Those snapshots keep a current baseline but NEVER turn a field
 * into a manual capture. The installed form-engine-portable 2.46.0 supplies
 * origin tags through `onFieldChange`; the hook feeds those events to
 * `observeFieldChange`. Unexplained/legacy values still remain unknown.
 */

export type FieldOrigin = 'ocr' | 'manual' | 'hydrated' | 'unknown';

export interface FieldProvenance {
  origin: FieldOrigin;
  userEdited: boolean;
  editedThisSession: boolean;
  extractionVersion: number | null;
}

/** Persisted alongside a draft. Bound to the values it describes by hash. */
export interface ProvenanceSnapshot {
  version: 1;
  /** Highest extraction request id issued in this tracker's lifetime. */
  extractionVersion: number;
  /** Hash of the tracked-field values the snapshot was taken from. */
  valuesHash: string;
  fields: Record<string, Omit<FieldProvenance, 'editedThisSession'>>;
}

/** Shape sent with a final submit — operational evidence only. */
export interface PassportProvenanceMetadata {
  version: 1;
  extractionVersion: number;
  fields: Array<{ field: string; origin: FieldOrigin; userEdited: boolean; extractionVersion: number | null }>;
}

/**
 * The public field-change shape planned by form-engine-portable. Kept local
 * while the published engine is 2.45.x, so this consumer does not import a
 * type or API that is not in its installed artifact.
 */
export interface PassportFieldChangeEvent {
  fieldName: string;
  value: unknown;
  previousValue?: unknown;
  changed: boolean;
  origin: 'user' | 'extraction' | 'hydration' | 'engine';
}

/**
 * Passport-step fields whose capture method matters to canonical promotion.
 * Literal keys accepted by the Hub 34424df passport metadata contract.
 * Nationality and issuing country are distinct: passport_country is issuing
 * country, never a fallback for passport_nationality. Aliases retain their own
 * literal value/evidence; tracking a field does not infer or copy its value.
 */
export const PASSPORT_PROVENANCE_FIELDS: readonly string[] = [
  'passport_name',
  'passport_surname',
  'passport_number',
  'passport_country',
  'passport_place_of_birth',
  'passport_date_of_birth',
  'passport_issue_date',
  'passport_expiry_date',
  'passport_nationality',
  'passportNumber',
  'passportDateOfBirth',
  'passport_dob',
  'passportDob',
  'passportNationality',
  'passportPlaceOfBirth',
  'passportCountry',
  'passportIssuingCountry',
  'passportIssueDate',
  'passportExpiryDate',
  'passportExpirationDate',
  'passport_upload',
  'passportUpload',
  'passport_scan',
  'passportScan',
];

const TRACKED = new Set(PASSPORT_PROVENANCE_FIELDS);

const isBlank = (v: unknown) =>
  v == null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

/** Deterministic hash of the tracked-field values (order-independent). */
export function hashTrackedValues(values: Record<string, unknown> | null | undefined): string {
  const src = values ?? {};
  const parts: string[] = [];
  for (const key of PASSPORT_PROVENANCE_FIELDS) {
    const v = src[key];
    if (isBlank(v)) continue;
    parts.push(`${key}=${typeof v === 'string' ? v : JSON.stringify(v)}`);
  }
  // djb2 — stable, dependency-free, collision-tolerant for this purpose (a
  // mismatch only downgrades restored provenance to 'unknown').
  let h = 5381;
  const s = parts.join('\u0000');
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return `v1:${(h >>> 0).toString(16)}:${parts.length}`;
}

const sameValue = (a: unknown, b: unknown) => {
  if (a === b) return true;
  if (isBlank(a) && isBlank(b)) return true;
  if (typeof a === 'object' || typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b);
  return false;
};

export interface PassportProvenanceTracker {
  /** Issue a new extraction request id and make it the current one. */
  beginExtraction(): number;
  /** True while `id` is the most recent, non-invalidated request. */
  isCurrentExtraction(id: number): boolean;
  /** Host UI cleanup follows the shared tracker, not a recreated handler. */
  onExtractionInvalidated(id: number, cleanup: () => void): (() => void) | undefined;
  /**
   * Document removed/replaced or an explicit reset while extraction may be
   * pending: every outstanding request becomes obsolete, and 'ocr' provenance
   * is dropped (the engine clears those values on removal).
   */
  invalidateExtractions(): void;
  /** Mark the synchronous clear caused by removing an upload as engine-owned. */
  beginDocumentRemoval(): void;
  finishDocumentRemoval(): void;
  /**
   * Guard an extraction payload before it is written to the form. Returns
   * null when the request is obsolete (caller must write nothing), otherwise
   * the payload minus any field the applicant has edited — a manual value is
   * never overwritten by OCR, even when the fields would otherwise be free.
   * `toField` maps payload keys to form field names.
   */
  guardExtraction<T extends Record<string, unknown>>(
    id: number,
    data: T,
    toField: (key: string) => string | undefined,
  ): Partial<T> | null;
  /**
   * Record that an accepted extraction wrote these field values. The tracker
   * remembers them so the engine's echo of the write is not misread as a user
   * edit. Ignored (returns false) for an obsolete request.
   */
  recordExtractionWrite(id: number, written: Record<string, unknown>): boolean;
  /**
   * Seed from the values the form is being mounted with. `saved` is the
   * snapshot persisted with the draft; it is honoured only when its hash
   * matches `values` (same snapshot), otherwise every present tracked value is
   * 'unknown' — legacy drafts are never given a fabricated capture method.
   */
  hydrate(values: Record<string, unknown>, saved?: ProvenanceSnapshot | null): void;
  /**
   * Feed a values-only engine snapshot. It is a baseline/reconciliation
   * operation only: it deliberately cannot infer a user action.
   */
  observeValues(values: Record<string, unknown>): void;
  /**
   * Feed an explicit engine field-origin event. Only a changed `user` event
   * is manual capture; hydration and unexplained engine writes never are.
   */
  observeFieldChange(event: PassportFieldChangeEvent): void;
  /** Successful save: the persisted edit flags are consumed; origins remain. */
  markSaved(): void;
  /** Explicit reset (new step mount without hydration). */
  reset(): void;
  get(field: string): FieldProvenance | undefined;
  snapshot(values: Record<string, unknown>): ProvenanceSnapshot;
  toSaveMetadata(values: Record<string, unknown>): PassportProvenanceMetadata | null;
}

export function createPassportProvenanceTracker(): PassportProvenanceTracker {
  let seq = 0;
  let current = 0;
  let extractionCleanup: (() => void) | undefined;
  const cleanExtraction = () => {
    const cleanup = extractionCleanup;
    extractionCleanup = undefined;
    cleanup?.();
  };
  let fields = new Map<string, FieldProvenance>();
  let lastValues: Record<string, unknown> | null = null;
  /** Values an accepted extraction wrote, awaiting the engine's echo. */
  const pendingEcho = new Map<string, unknown>();
  // Legacy engine writes have no origin. Protect changed fields without
  // fabricating manual evidence; publication of field-origin events removes
  // this conservative overwrite limitation.
  const unexplainedChanges = new Set<string>();
  const removalClears = new Set<string>();

  const ensure = (field: string): FieldProvenance => {
    let p = fields.get(field);
    if (!p) {
      p = { origin: 'unknown', userEdited: false, editedThisSession: false, extractionVersion: null };
      fields.set(field, p);
    }
    return p;
  };

  const tracker: PassportProvenanceTracker = {
    beginExtraction() {
      seq += 1;
      current = seq;
      const id = seq;
      cleanExtraction();
      return id;
    },
    isCurrentExtraction(id) {
      return id === current && id !== 0;
    },
    onExtractionInvalidated(id, cleanup) {
      if (!tracker.isCurrentExtraction(id)) return undefined;
      extractionCleanup = cleanup;
      return () => {
        if (extractionCleanup === cleanup) extractionCleanup = undefined;
      };
    },
    invalidateExtractions() {
      current = 0;
      cleanExtraction();
      pendingEcho.clear();
      for (const [field, p] of fields) {
        if (p.origin !== 'ocr' || p.userEdited) continue;
        fields.delete(field);
        // The engine clears extracted values AFTER onDeleteDocument fires.
        // Move the baseline to "blank" now so that clear is observed as a
        // no-op, not as the applicant emptying the field (which would mark
        // it manual and make the guard refuse the next upload's OCR).
        if (lastValues) lastValues = { ...lastValues, [field]: undefined };
      }
    },
    beginDocumentRemoval() {
      tracker.invalidateExtractions();
      for (const field of PASSPORT_PROVENANCE_FIELDS) removalClears.add(field);
    },
    finishDocumentRemoval() {
      removalClears.clear();
    },
    guardExtraction(id, data, toField) {
      if (!tracker.isCurrentExtraction(id)) return null;
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        const field = toField(key);
        // A manual value is authoritative over any later OCR response,
        // including after it has been saved/reloaded (when userEdited is
        // consumed but origin remains manual).
        if (field && (
          fields.get(field)?.userEdited || fields.get(field)?.origin === 'manual' ||
          unexplainedChanges.has(field) ||
          (fields.get(field)?.origin === 'unknown' && !isBlank(lastValues?.[field]))
        )) continue;
        out[key] = value;
      }
      return out as Partial<typeof data>;
    },
    recordExtractionWrite(id, written) {
      if (!tracker.isCurrentExtraction(id)) return false;
      for (const [field, value] of Object.entries(written)) {
        if (!TRACKED.has(field)) continue;
        const p = ensure(field);
        p.origin = 'ocr';
        p.userEdited = false;
        p.editedThisSession = false;
        p.extractionVersion = id;
        unexplainedChanges.delete(field);
        pendingEcho.set(field, value);
        if (lastValues) lastValues = { ...lastValues, [field]: value };
      }
      return true;
    },
    hydrate(values, saved) {
      current = 0;
      cleanExtraction();
      fields = new Map();
      pendingEcho.clear();
      unexplainedChanges.clear();
      current = 0;
      const restorable = saved && saved.version === 1 && saved.valuesHash === hashTrackedValues(values);
      if (restorable) {
        seq = Math.max(seq, saved.extractionVersion);
        for (const [field, p] of Object.entries(saved.fields)) {
          if (!TRACKED.has(field) || (isBlank(values[field]) && !p.userEdited && p.origin !== 'manual')) continue;
          // Restore the saved origin exactly — a legacy 'unknown' must not
          // become 'hydrated' just because it round-tripped through a draft.
          fields.set(field, {
            origin: p.origin === 'ocr' || p.origin === 'manual' || p.origin === 'unknown' ? p.origin : 'hydrated',
            userEdited: Boolean(p.userEdited),
            editedThisSession: false,
            extractionVersion: typeof p.extractionVersion === 'number' ? p.extractionVersion : null,
          });
        }
      } else {
        for (const field of PASSPORT_PROVENANCE_FIELDS) {
          if (!isBlank(values[field])) {
            fields.set(field, { origin: 'unknown', userEdited: false, editedThisSession: false, extractionVersion: null });
          }
        }
      }
      lastValues = { ...values };
    },
    observeValues(values) {
      if (!lastValues) {
        // No hydrate() happened — the first observation IS the baseline. A
        // values-only snapshot has no actor/origin evidence.
        lastValues = { ...values };
        return;
      }
      for (const field of PASSPORT_PROVENANCE_FIELDS) {
        if (removalClears.has(field) && isBlank(values[field])) {
          // 2.45.x clears ALL passport fields on removal. These blanks are
          // explained, not unknown edits that should block a future upload.
          fields.delete(field);
          unexplainedChanges.delete(field);
          continue;
        }
        if (sameValue(lastValues[field], values[field])) continue;
        if (pendingEcho.has(field) && sameValue(pendingEcho.get(field), values[field])) {
          pendingEcho.delete(field);
          continue;
        }
        // The old origin described the old value, not this unexplained write.
        // Protect it from OCR, but never call it an applicant edit.
        fields.delete(field);
        if (!isBlank(values[field])) ensure(field);
        unexplainedChanges.add(field);
      }
      lastValues = { ...values };
    },
    observeFieldChange(event) {
      if (!TRACKED.has(event.fieldName) || !event.changed) return;
      // The engine only tags a write origin:'extraction' when the caller used
      // its own tagged-write API. A caller that wrote the form directly (no
      // tagging) still gets an echo event here, untagged. recordExtractionWrite
      // already staged this exact value in pendingEcho for that case -- treat
      // it the same way observeValues does, or the echo downgrades a just-set
      // 'ocr' field back to 'unknown'.
      if (pendingEcho.has(event.fieldName) && sameValue(pendingEcho.get(event.fieldName), event.value)) {
        pendingEcho.delete(event.fieldName);
        if (lastValues) lastValues = { ...lastValues, [event.fieldName]: event.value };
        return;
      }
      const p = ensure(event.fieldName);
      if (event.origin === 'user') {
        unexplainedChanges.delete(event.fieldName);
        p.origin = 'manual';
        p.userEdited = true;
        p.editedThisSession = true;
        p.extractionVersion = null;
      } else if (event.origin === 'extraction') {
        p.origin = 'ocr';
        p.userEdited = false;
        p.editedThisSession = false;
        // requestId is not canonical evidence. The host's accepted extraction
        // write records its own local request version via recordExtractionWrite.
      } else if (event.origin === 'hydration') {
        if (!sameValue(lastValues?.[event.fieldName], event.value)) {
          // hydrate(values, saved) must establish any persisted evidence first.
          // An unseeded hydration event cannot transfer the old value's origin.
          fields.delete(event.fieldName);
          if (!isBlank(event.value)) ensure(event.fieldName);
        } else {
          p.editedThisSession = false;
        }
      } else {
        // The previous origin belongs to the previous value. An engine write
        // (including a derived value or conditional clear) is never manual.
        fields.delete(event.fieldName);
        if (!isBlank(event.value)) ensure(event.fieldName);
        unexplainedChanges.add(event.fieldName);
      }
      if (lastValues) lastValues = { ...lastValues, [event.fieldName]: event.value };
    },
    markSaved() {
      for (const p of fields.values()) {
        p.userEdited = false;
        p.editedThisSession = false;
      }
    },
    reset() {
      current = 0;
      cleanExtraction();
      fields = new Map();
      pendingEcho.clear();
      unexplainedChanges.clear();
      removalClears.clear();
      lastValues = null;
      current = 0;
    },
    get(field) {
      const p = fields.get(field);
      return p ? { ...p } : undefined;
    },
    snapshot(values) {
      tracker.observeValues(values);
      const out: ProvenanceSnapshot['fields'] = {};
      for (const [field, p] of fields) {
        if (isBlank(values[field]) && !p.userEdited && p.origin !== 'manual') continue;
        out[field] = { origin: p.origin, userEdited: p.userEdited, extractionVersion: p.extractionVersion };
      }
      return { version: 1, extractionVersion: seq, valuesHash: hashTrackedValues(values), fields: out };
    },
    toSaveMetadata(values) {
      tracker.observeValues(values);
      const entries: PassportProvenanceMetadata['fields'] = [];
      for (const field of PASSPORT_PROVENANCE_FIELDS) {
        if (isBlank(values[field])) continue;
        const p = fields.get(field);
        entries.push({
          field,
          origin: p?.origin ?? 'unknown',
          userEdited: p?.userEdited ?? false,
          extractionVersion: p?.extractionVersion ?? null,
        });
      }
      if (entries.length === 0) return null;
      return { version: 1, extractionVersion: seq, fields: entries };
    },
  };
  return tracker;
}

/** Steps whose values this tracker applies to. */
export const isPassportStepKey = (stepKey: string | undefined | null) =>
  typeof stepKey === 'string' && stepKey.includes('passport');
